import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useSocket } from './SocketContext';

export const AdminAuthContext = createContext();

export const AdminAuthProvider = ({ children }) => {
  const socket = useSocket();
  const [admin, setAdmin] = useState(() => {
    const savedData = localStorage.getItem('adminData');
    return savedData ? JSON.parse(savedData) : null;
  });

  // Connetti socket quando l'admin è presente
  useEffect(() => {
    if (admin && socket) {
      const token = localStorage.getItem('adminToken');
      if (token) {
        console.log('🔌 Inizializzando socket per admin:', admin.id);
        const socketInstance = socket.connect(token, true); // true = isAdmin
        
        // Aspetta l'evento 'connect' per fare join al room
        if (socketInstance) {
          const handleConnect = () => {
            console.log('🔌 Socket connesso, admin fa join al room becco_' + admin.id);
            socket.joinBeccoRoom(admin.id);
          };
          
          // Se già connesso, fai subito il join
          if (socketInstance.connected) {
            handleConnect();
          } else {
            // Altrimenti aspetta la connessione
            socketInstance.on('connect', handleConnect);
            
            // Cleanup
            return () => {
              socketInstance.off('connect', handleConnect);
            };
          }
        }
      }
    }
  }, [admin, socket]);

  // Funzione per tradurre gli errori HTTP in messaggi user-friendly
  const handleAuthError = (error) => {
    if (error.response) {
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (serverMessage) {
        throw new Error(serverMessage);
      }

      switch (error.response.status) {
        case 400:
          throw new Error('Username o password non validi');
        case 401:
          throw new Error('Non autorizzato: verifica le tue credenziali');
        case 403:
          throw new Error('Non hai i permessi necessari');
        case 404:
          throw new Error('Account non trovato');
        case 409:
          throw new Error('Username già in uso');
        default:
          throw new Error('Errore durante l\'autenticazione');
      }
    } else if (error.request) {
      throw new Error('Impossibile contattare il server');
    } else {
      throw new Error(error.message || 'Errore durante l\'autenticazione');
    }
  };

  // Funzione per il login
  const login = async (username, password, useMySQL = true) => {
    try {
      const endpoint = useMySQL ? '/api/mysql/admin/auth/login' : '/api/admin/auth/login';
      const response = await axios.post(endpoint, { username, password });
      const adminData = response.data.admin;
      const token = response.data.token;
      
      setAdmin(adminData);
      localStorage.setItem('adminData', JSON.stringify(adminData));
      localStorage.setItem('adminToken', token); // Salva il token JWT
      localStorage.setItem('adminAuthType', useMySQL ? 'mysql' : 'mongodb');
      return response.data;
    } catch (error) {
      handleAuthError(error);
    }
  };

  // Funzione per la registrazione
  const register = async (username, password, secretKey, nome, cognome, useMySQL = true) => {
    try {
      const endpoint = useMySQL ? '/api/mysql/admin/auth/register' : '/api/admin/auth/register';
      const response = await axios.post(endpoint, { 
        username, 
        password, 
        secretKey,
        nome,
        cognome
      });
      return response.data;
    } catch (error) {
      handleAuthError(error);
    }
  };

  // Funzione per il logout
  const logout = () => {
    setAdmin(null);
    localStorage.removeItem('adminData');
    localStorage.removeItem('adminToken'); // Rimuovi anche il token
    localStorage.removeItem('adminAuthType');
    
    // Disconnetti socket
    if (socket) {
      console.log('🔌 Disconnettendo socket per admin');
      socket.disconnect();
    }
  };

  return (
    <AdminAuthContext.Provider value={{ admin, login, register, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};
